/**
 * Direct browser → Meilisearch HTTP client. No SDK dependency: uses
 * the browser's `fetch` against Meili's REST API with a search-only
 * scoped key bundled into the FE build.
 *
 * Env vars (all `REACT_APP_` so CRA bundles them client-side):
 *   - `REACT_APP_MEILISEARCH_HOST`: origin, e.g. `https://search.cys.example`
 *   - `REACT_APP_MEILISEARCH_SEARCH_KEY`: search-only key scoped to the listings index
 *   - `REACT_APP_MEILISEARCH_LISTINGS_INDEX_UID`: defaults to `listings`
 */

import type { MeiliSearchBody, MeiliSearchResponse } from './meilisearch';

const HOST_ENV = 'REACT_APP_MEILISEARCH_HOST';
const SEARCH_KEY_ENV = 'REACT_APP_MEILISEARCH_SEARCH_KEY';
const INDEX_UID_ENV = 'REACT_APP_MEILISEARCH_LISTINGS_INDEX_UID';
const DEFAULT_INDEX_UID = 'listings';

export class MeilisearchNotConfiguredError extends Error {
  code = 'MEILISEARCH_NOT_CONFIGURED' as const;
  status = 503 as const;
  constructor() {
    super('Meilisearch is not configured (missing host or search key)');
    this.name = 'MeilisearchNotConfiguredError';
  }
}

export class MeilisearchUpstreamError extends Error {
  code = 'MEILISEARCH_UPSTREAM' as const;
  status = 502 as const;
  upstreamStatus: number;
  /** Meili's own error code (e.g. `invalid_search_filter`) when the body parses as JSON. */
  meiliCode?: string;
  /** The search body that provoked the error, for diagnosing which clause was rejected. */
  requestBody?: MeiliSearchBody;
  constructor(upstreamStatus: number, body: string, requestBody?: MeiliSearchBody) {
    let meiliCode: string | undefined;
    try {
      meiliCode = (JSON.parse(body) as { code?: string }).code;
    } catch {
      meiliCode = undefined;
    }
    super(`Meilisearch responded ${upstreamStatus}${meiliCode ? ` (${meiliCode})` : ''}: ${body.slice(0, 200)}`);
    this.name = 'MeilisearchUpstreamError';
    this.upstreamStatus = upstreamStatus;
    this.meiliCode = meiliCode;
    this.requestBody = requestBody;
  }
}

type Config = { host: string; searchKey: string; indexUid: string };

const requireConfig = (): Config => {
  const host = process.env[HOST_ENV];
  const searchKey = process.env[SEARCH_KEY_ENV];
  const indexUid = process.env[INDEX_UID_ENV] || DEFAULT_INDEX_UID;
  if (!host || !searchKey) throw new MeilisearchNotConfiguredError();
  return { host: host.replace(/\/$/, ''), searchKey, indexUid };
};

/**
 * POST a search body to Meilisearch's `/indexes/{uid}/search` endpoint
 * direct from the browser using the bundled search-only key.
 *
 * @throws {MeilisearchNotConfiguredError} when env vars are unset
 * @throws {MeilisearchUpstreamError} when Meili returns non-2xx
 */
export const searchListingsIndex = async (
  body: MeiliSearchBody
): Promise<MeiliSearchResponse> => {
  const { host, searchKey, indexUid } = requireConfig();
  const url = `${host}/indexes/${encodeURIComponent(indexUid)}/search`;

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${searchKey}`,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new MeilisearchUpstreamError(res.status, text, body);
  }

  return res.json() as Promise<MeiliSearchResponse>;
};
